import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  DialogContentText,
  FormControlLabel,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Notifications as NotificationsIcon,
  Assessment as AssessmentIcon,
  BarChart as BarChartIcon,
  Backup as BackupIcon,
  Api as ApiIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  VpnKey as VpnKeyIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Pool as PoolIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Watch as WatchIcon,
  MonitorHeart as MonitorHeartIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  Restore as RestoreIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  ModelTraining as ModelTrainingIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { attachAuthInterceptors } from '../../services/authTokenRefresh';
import ChangeControlCenterPasswordDialog from './ChangeControlCenterPasswordDialog';
import AddAdminDialog from './AddAdminDialog';
import EditAdminDialog from './EditAdminDialog';
import DeleteAdminDialog from './DeleteAdminDialog';
import { API_ORIGIN } from '../../config';
import type { SystemSettings as ApiSystemSettings } from '../../types/system';
import {
  DEFAULT_MONITORING_AI_SETTINGS,
  mergeMonitoringAiSettings,
  type MonitoringAiSettings,
} from '../../constants/monitoringAiSettings';
import MonitoringAiSettingsCard from './MonitoringAiSettingsCard';
import YoloDetectorSettingsCard from './YoloDetectorSettingsCard';
import RiskRecordingSettingsCard from './RiskRecordingSettingsCard';
import RiskLandThresholdsCard from './RiskLandThresholdsCard';
import ThresholdUnifyCard from './ThresholdUnifyCard';
import DetectionLabelTrainingCard from './DetectionLabelTrainingCard';
import AppAccessKeySettingsCard from './AppAccessKeySettingsCard';
import PrimaryPoolsSettingsCard from './PrimaryPoolsSettingsCard';
import AnonymousBenchmarkWidget from '../dashboard/AnonymousBenchmarkWidget';
import SystemReadinessWidget from '../dashboard/SystemReadinessWidget';
import EventPipelineLatencyWidget from '../dashboard/EventPipelineLatencyWidget';
import { useAuth } from '../../contexts/AuthContext';
import { useKeepGrantedTab, useMenuPolicy } from '../../brand/MenuPolicyContext';
import { SYSTEM_TAB_IDS, hiddenTabProps } from '../../brand/menuCatalog';
import { invalidateMonitoringSettingsCache } from '../../services/api';
import { extractArrayFromApiPayload } from '../../utils/api';
import {
  DEFAULT_RISK_RECORDING_SETTINGS,
  mergeRiskRecordingSettings,
  type RiskRecordingSettings,
} from '../../constants/riskRecordingSettings';
import {
  DEFAULT_RISK_LAND_THRESHOLDS,
  mergeRiskLandThresholds,
  type RiskLandThresholds,
} from '../../constants/riskLandThresholds';
import {
  DEFAULT_SAFETY_SMS_SETTINGS,
  mergeSafetySmsSettings,
  type SafetySmsSettings,
} from '../../constants/safetySmsSettings';
import {
  mergeSafetyKakaoSettings,
  DEFAULT_SAFETY_KAKAO_SETTINGS,
  type SafetyKakaoSettings,
} from '../../constants/safetyKakaoSettings';
import {
  mergeWearableAlertSettings,
  DEFAULT_WEARABLE_ALERT_SETTINGS,
  type WearableAlertSettings,
} from '../../constants/wearableAlertSettings';
import {
  mergeControlRoomSirenSettings,
  DEFAULT_CONTROL_ROOM_SIREN_SETTINGS,
  type ControlRoomSirenSettings,
} from '../../constants/controlRoomSirenSettings';
import {
  mergeControlArchiveSettings,
  type ControlArchiveSettings,
} from '../../constants/controlArchiveSettings';
import { safetyAlertSettingsCardSx } from '../../constants/safetyAlertSettingsUi';
import {
  mergeSystemNotificationSettings,
  DEFAULT_SYSTEM_NOTIFICATION_SETTINGS,
} from '../../constants/notificationSettings';
import SafetySmsSettingsCard from './SafetySmsSettingsCard';
import SafetyKakaoSettingsCard from './SafetyKakaoSettingsCard';
import WearableAlertSettingsCard from './WearableAlertSettingsCard';
import ControlRoomSirenSettingsCard from './ControlRoomSirenSettingsCard';
import SafetyAlertDispatchHistoryCard from '../safety/SafetyAlertDispatchHistoryCard';
import AiServerResourceMonitor from './AiServerResourceMonitor';

function buildApiSystemSettings(data: Record<string, unknown> | null | undefined): ApiSystemSettings {
  const d = data || {};
  const network = (d.network as Record<string, unknown>) || {};
  return {
    general: (d.general as ApiSystemSettings['general']) || {
      system_name: 'Pool Control System',
      timezone: 'Asia/Seoul',
      language: 'ko',
      date_format: 'YYYY-MM-DD',
    },
    security: (d.security as ApiSystemSettings['security']) || {
      session_timeout: 30,
      max_login_attempts: 5,
      password_expiry_days: 90,
      two_factor_auth: false,
    },
    notifications: mergeSystemNotificationSettings(
      (d.notifications as Record<string, unknown>) || undefined
    ),
    storage: (d.storage as ApiSystemSettings['storage']) || {
      data_retention_days: 365,
      backup_frequency: 'daily',
      backup_time: '00:00',
      auto_cleanup: true,
    },
    network: {
      websocket_timeout: Number(network.websocket_timeout ?? 30),
      api_rate_limit: Number(network.api_rate_limit ?? 100),
      max_connections: Number(network.max_connections ?? 1000),
      ssl_enabled: network.ssl_enabled !== false,
      use_remote_playback: Boolean(network.use_remote_playback),
      public_api_base_url: String(network.public_api_base_url ?? ''),
    },
    monitoring_ai: mergeMonitoringAiSettings(
      (d.monitoring_ai as Partial<MonitoringAiSettings>) || undefined
    ),
    risk_recording: mergeRiskRecordingSettings(
      (d.risk_recording as Partial<RiskRecordingSettings>) || undefined
    ),
    risk_thresholds: mergeRiskLandThresholds(
      (d.risk_thresholds as Partial<RiskLandThresholds>) || undefined
    ),
    safety_sms: mergeSafetySmsSettings(
      (d.safety_sms as Partial<SafetySmsSettings>) || undefined
    ),
    safety_kakao: mergeSafetyKakaoSettings(
      (d.safety_kakao as Partial<SafetyKakaoSettings>) || undefined
    ),
    wearable_alert: mergeWearableAlertSettings(
      (d.wearable_alert as Partial<WearableAlertSettings>) || undefined
    ),
    control_room_siren: mergeControlRoomSirenSettings(
      (d.control_room_siren as Partial<ControlRoomSirenSettings>) || undefined
    ),
    control_archive: mergeControlArchiveSettings(
      (d.control_archive as Partial<ControlArchiveSettings>) || undefined
    ),
    dashboard: {
      primary_pool_codes: Array.isArray(
        (d.dashboard as { primary_pool_codes?: unknown })?.primary_pool_codes
      )
        ? (d.dashboard as { primary_pool_codes: unknown[] }).primary_pool_codes.map((c) =>
            String(c)
          )
        : [],
      primary_pools: Array.isArray(
        (d.dashboard as { primary_pools?: unknown })?.primary_pools
      )
        ? (d.dashboard as { primary_pools: { code?: string; order?: number }[] }).primary_pools
            .filter((x) => x?.code)
            .map((x) => ({
              code: String(x.code),
              order: Number(x.order) || 0,
            }))
        : [],
    },
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`system-tabpanel-${index}`}
      aria-labelledby={`system-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface Admin {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  is_superuser: boolean;
  is_brand_manager?: boolean;
  is_main_admin?: boolean;
  last_login: string;
  monitoring_pool_ids?: number[];
  monitor_all_pools?: boolean;
}

// axios 인스턴스 생성
const api = axios.create({
  baseURL: API_ORIGIN,
  headers: {
    'Content-Type': 'application/json'
  }
});

attachAuthInterceptors(api);

const SystemManagement: React.FC = () => {
  const { authState } = useAuth();
  const { allowNav } = useMenuPolicy();
  const isSuperuser = Boolean(authState.user?.is_superuser);
  const [activeTab, setActiveTab] = useState(0);
  const systemTabOn = useKeepGrantedTab(activeTab, SYSTEM_TAB_IDS, setActiveTab);
  const [settings, setSettings] = useState({
    system: {
      language: 'ko',
      timezone: 'Asia/Seoul',
      dateFormat: 'YYYY-MM-DD',
      timeFormat: '24h'
    },
    security: {
      passwordPolicy: {
        minLength: 8,
        requireSpecialChar: true,
        requireNumber: true,
        requireUppercase: true
      },
      sessionTimeout: 30,
      maxLoginAttempts: 5
    },
    backup: {
      autoBackup: false,
      backupInterval: 'daily',
      backupTime: '02:00',
      backupWeekday: 'monday',
      retentionPeriod: 30,
      backupLocation: 'local'
    },
    notifications: {
      email: true,
      sms: false,
      push: true,
      alertLevel: 'medium'
    },
    passwords: {
      controlRoom: {
        enabled: true,
        lastChanged: '2024-03-20',
        changeInterval: 90, // days
        complexity: 'high'
      },
      pool: {
        enabled: true,
        lastChanged: '2024-03-20',
        changeInterval: 30, // days
        complexity: 'medium'
      },
      admin: {
        enabled: true,
        lastChanged: '2024-03-20',
        changeInterval: 30, // days
        complexity: 'medium'
      }
    },
    admins: [
      {
        id: 1,
        name: '관리자1',
        role: 'super',
        lastLogin: '2024-03-20 15:30',
        status: 'active'
      },
      {
        id: 2,
        name: '관리자2',
        role: 'admin',
        lastLogin: '2024-03-20 14:20',
        status: 'active'
      }
    ],
    pools: [
      {
        id: 1,
        code: 'P001',
        name: '시설 1',
        lastPasswordChange: '2024-03-20',
        passwordStatus: 'active'
      },
      {
        id: 2,
        code: 'P002',
        name: '시설 2',
        lastPasswordChange: '2024-03-20',
        passwordStatus: 'active'
      }
    ],
    wearables: {
      monitoring: {
        enabled: true,
        updateInterval: 5, // seconds
        alertThreshold: {
          heartRate: { min: 40, max: 160 },
          spo2: { min: 92 },
          bloodPressure: { systolic: { min: 90, max: 140 }, diastolic: { min: 60, max: 90 } }
        }
      },
      devices: [
        {
          id: 'W001',
          name: '웨어러블 1',
          status: 'active',
          battery: 85,
          lastSync: '2024-03-20 15:30',
          member: '홍길동',
          currentData: {
            heartRate: 75,
            spo2: 98,
            bloodPressure: { systolic: 120, diastolic: 80 },
            temperature: 36.5,
            motion: 'normal'
          }
        },
        {
          id: 'W002',
          name: '웨어러블 2',
          status: 'active',
          battery: 92,
          lastSync: '2024-03-20 15:25',
          member: '김철수',
          currentData: {
            heartRate: 82,
            spo2: 97,
            bloodPressure: { systolic: 118, diastolic: 78 },
            temperature: 36.6,
            motion: 'normal'
          }
        }
      ],
      alerts: [
        {
          id: 1,
          deviceId: 'W001',
          type: 'heartRate',
          level: 'warning',
          message: '심박수 비정상 감지',
          timestamp: '2024-03-20 15:28',
          status: 'active'
        },
        {
          id: 2,
          deviceId: 'W002',
          type: 'spo2',
          level: 'critical',
          message: '산소포화도 급격한 저하',
          timestamp: '2024-03-20 15:25',
          status: 'active'
        }
      ]
    }
  });
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [pools, setPools] = useState<any[]>([]);
  const [loadingPools, setLoadingPools] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedPool, setSelectedPool] = useState<any>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [bulkPasswordDialogOpen, setBulkPasswordDialogOpen] = useState(false);
  const [bulkNewPassword, setBulkNewPassword] = useState('');
  const [bulkConfirmPassword, setBulkConfirmPassword] = useState('');
  const [bulkPasswordError, setBulkPasswordError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [backupHistory, setBackupHistory] = useState<any[]>([]);
  const [backupSettingsSaving, setBackupSettingsSaving] = useState(false);
  const [nextBackupAt, setNextBackupAt] = useState('');
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<any>(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [deleteBackupDialogOpen, setDeleteBackupDialogOpen] = useState(false);
  const [deleteAllBackupsDialogOpen, setDeleteAllBackupsDialogOpen] = useState(false);
  const [backupPendingDelete, setBackupPendingDelete] = useState<any>(null);
  const [isDeletingBackup, setIsDeletingBackup] = useState(false);
  const [controlCenterPassword, setControlCenterPassword] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [controlCenterPasswordError, setControlCenterPasswordError] = useState('');
  const [serverSettings, setServerSettings] = useState<ApiSystemSettings | null>(null);
  const [gatewaySettingsSaving, setGatewaySettingsSaving] = useState(false);
  const [monitoringAiSaving, setMonitoringAiSaving] = useState(false);
  const [riskRecordingSaving, setRiskRecordingSaving] = useState(false);
  const [riskThresholdsSaving, setRiskThresholdsSaving] = useState(false);
  const [safetySmsSaving, setSafetySmsSaving] = useState(false);
  const [safetyKakaoSaving, setSafetyKakaoSaving] = useState(false);
  const [wearableAlertSaving, setWearableAlertSaving] = useState(false);
  const [controlRoomSirenSaving, setControlRoomSirenSaving] = useState(false);
  const [notificationChannelsSaving, setNotificationChannelsSaving] = useState(false);
  const [yoloModelFiles, setYoloModelFiles] = useState<string[]>([]);
  const [yoloRuntimeStatus, setYoloRuntimeStatus] = useState<
    Record<string, unknown> | undefined
  >(undefined);

  const fetchYoloModelFiles = async () => {
    try {
      const response = await api.get('/api/settings/yolo-model-files');
      setYoloModelFiles(Array.isArray(response.data?.files) ? response.data.files : []);
      setYoloRuntimeStatus(response.data?.status);
    } catch (error) {
      console.error('YOLO 모델 목록 로드 실패:', error);
    }
  };

  const fetchServerSettings = async () => {
    try {
      const response = await api.get('/api/settings/system');
      setServerSettings(buildApiSystemSettings(response.data));
      await fetchYoloModelFiles();
    } catch (error) {
      console.error('시스템 설정 로드 실패:', error);
      setServerSettings(buildApiSystemSettings({}));
    }
  };

  const handleGatewayNetworkChange = (
    field: 'use_remote_playback' | 'public_api_base_url',
    value: boolean | string
  ) => {
    setServerSettings((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        network: {
          ...prev.network,
          [field]: value,
        },
      };
    });
  };

  const handleMonitoringAiChange = (next: MonitoringAiSettings) => {
    setServerSettings((prev) => (prev ? { ...prev, monitoring_ai: next } : prev));
  };

  const handleRiskRecordingChange = (next: RiskRecordingSettings) => {
    setServerSettings((prev) => (prev ? { ...prev, risk_recording: next } : prev));
  };

  const handleNotificationChannelChange = (
    key:
      | 'safety_channel_sms'
      | 'safety_channel_kakao'
      | 'safety_channel_wearable'
      | 'event_share_image_enabled'
      | 'kakao_bot_send_enabled',
    value: boolean
  ) => {
    setServerSettings((prev) => {
      if (!prev) return prev;
      const notifications = {
        ...mergeSystemNotificationSettings(prev.notifications),
        [key]: value,
      };
      const next = { ...prev, notifications };
      if (key === 'safety_channel_kakao' && prev.safety_kakao) {
        next.safety_kakao = { ...prev.safety_kakao, enabled: value };
      }
      if (key === 'safety_channel_wearable' && prev.wearable_alert) {
        next.wearable_alert = { ...prev.wearable_alert, enabled: value };
      }
      return next;
    });
  };

  const saveNotificationChannelSettings = async () => {
    if (!serverSettings) return;
    setNotificationChannelsSaving(true);
    try {
      const response = await api.post('/api/settings/system', serverSettings);
      setServerSettings(buildApiSystemSettings(response.data));
      enqueueSnackbar('알림 채널 설정이 저장 완료', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || error?.message || '설정 저장에 실패',
        { variant: 'error' }
      );
    } finally {
      setNotificationChannelsSaving(false);
    }
  };

  const handleSafetySmsChange = (next: SafetySmsSettings) => {
    setServerSettings((prev) => (prev ? { ...prev, safety_sms: next } : prev));
  };

  const handleSafetyKakaoChange = (next: SafetyKakaoSettings) => {
    setServerSettings((prev) => (prev ? { ...prev, safety_kakao: next } : prev));
  };

  const handleWearableAlertChange = (next: WearableAlertSettings) => {
    setServerSettings((prev) => (prev ? { ...prev, wearable_alert: next } : prev));
  };

  const resetSafetyKakaoToDefaults = () => {
    setServerSettings((prev) =>
      prev ? { ...prev, safety_kakao: { ...DEFAULT_SAFETY_KAKAO_SETTINGS } } : prev
    );
    enqueueSnackbar('화면에 기본값을 불러옴. 저장을 눌러야 서버에 반영됨.', {
      variant: 'info',
    });
  };

  const resetWearableAlertToDefaults = () => {
    setServerSettings((prev) =>
      prev ? { ...prev, wearable_alert: { ...DEFAULT_WEARABLE_ALERT_SETTINGS } } : prev
    );
    enqueueSnackbar('화면에 기본값을 불러옴. 저장을 눌러야 서버에 반영됨.', {
      variant: 'info',
    });
  };

  const saveSafetyKakaoSettings = async () => {
    if (!serverSettings) return;
    setSafetyKakaoSaving(true);
    try {
      const response = await api.post('/api/settings/system', serverSettings);
      setServerSettings(buildApiSystemSettings(response.data));
      enqueueSnackbar('카카오 알림톡 설정이 저장 완료', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || error?.message || '설정 저장에 실패',
        { variant: 'error' }
      );
    } finally {
      setSafetyKakaoSaving(false);
    }
  };

  const saveWearableAlertSettings = async () => {
    if (!serverSettings) return;
    setWearableAlertSaving(true);
    try {
      const response = await api.post('/api/settings/system', serverSettings);
      setServerSettings(buildApiSystemSettings(response.data));
      enqueueSnackbar('웨어러블 알림 설정이 저장 완료', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || error?.message || '설정 저장에 실패',
        { variant: 'error' }
      );
    } finally {
      setWearableAlertSaving(false);
    }
  };

  const handleControlRoomSirenChange = (next: ControlRoomSirenSettings) => {
    setServerSettings((prev) => (prev ? { ...prev, control_room_siren: next } : prev));
  };

  const resetControlRoomSirenToDefaults = () => {
    setServerSettings((prev) =>
      prev
        ? { ...prev, control_room_siren: { ...DEFAULT_CONTROL_ROOM_SIREN_SETTINGS } }
        : prev
    );
    enqueueSnackbar('화면에 기본값을 불러옴. 저장을 눌러야 서버에 반영됨.', {
      variant: 'info',
    });
  };

  const saveControlRoomSirenSettings = async () => {
    if (!serverSettings) return;
    setControlRoomSirenSaving(true);
    try {
      const response = await api.post('/api/settings/system', serverSettings);
      setServerSettings(buildApiSystemSettings(response.data));
      enqueueSnackbar('관제실 경광등 설정이 저장 완료', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || error?.message || '설정 저장에 실패',
        { variant: 'error' }
      );
    } finally {
      setControlRoomSirenSaving(false);
    }
  };

  const resetSafetySmsToDefaults = () => {
    setServerSettings((prev) =>
      prev ? { ...prev, safety_sms: { ...DEFAULT_SAFETY_SMS_SETTINGS } } : prev
    );
    enqueueSnackbar('화면에 기본값을 불러옴. 저장을 눌러야 서버에 반영됨.', {
      variant: 'info',
    });
  };

  const saveSafetySmsSettings = async () => {
    if (!serverSettings) return;
    setSafetySmsSaving(true);
    try {
      const response = await api.post('/api/settings/system', serverSettings);
      setServerSettings(buildApiSystemSettings(response.data));
      enqueueSnackbar('현장 안전 SMS 설정이 저장 완료', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || error?.message || '설정 저장에 실패',
        { variant: 'error' }
      );
    } finally {
      setSafetySmsSaving(false);
    }
  };

  const resetRiskRecordingToDefaults = () => {
    setServerSettings((prev) =>
      prev
        ? { ...prev, risk_recording: { ...DEFAULT_RISK_RECORDING_SETTINGS } }
        : prev
    );
    enqueueSnackbar('화면에 기본값을 불러옴. 저장을 눌러야 서버에 반영됨.', {
      variant: 'info',
    });
  };

  const saveRiskRecordingSettings = async () => {
    if (!serverSettings) return;
    setRiskRecordingSaving(true);
    try {
      await api.post('/api/settings/system', serverSettings);
      enqueueSnackbar('이벤트 발생 시 영상 보관 설정이 저장 완료', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || error?.message || '설정 저장에 실패',
        { variant: 'error' }
      );
    } finally {
      setRiskRecordingSaving(false);
    }
  };

  const handleRiskThresholdsChange = (next: RiskLandThresholds) => {
    setServerSettings((prev) => (prev ? { ...prev, risk_thresholds: next } : prev));
  };

  const resetRiskThresholdsToDefaults = () => {
    setServerSettings((prev) =>
      prev
        ? { ...prev, risk_thresholds: { ...DEFAULT_RISK_LAND_THRESHOLDS } }
        : prev
    );
    enqueueSnackbar('화면에 기본값을 불러옴. 저장을 눌러야 서버에 반영됨.', {
      variant: 'info',
    });
  };

  const saveRiskThresholdsSettings = async () => {
    if (!serverSettings) return;
    setRiskThresholdsSaving(true);
    try {
      const response = await api.post('/api/settings/system', serverSettings);
      setServerSettings(buildApiSystemSettings(response.data));
      enqueueSnackbar('위험 판정 임계값이 저장 완료', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || error?.message || '설정 저장에 실패',
        { variant: 'error' }
      );
    } finally {
      setRiskThresholdsSaving(false);
    }
  };

  const resetMonitoringAiToDefaults = () => {
    setServerSettings((prev) =>
      prev
        ? { ...prev, monitoring_ai: { ...DEFAULT_MONITORING_AI_SETTINGS } }
        : prev
    );
    enqueueSnackbar('화면에 기본값을 불러옴. 저장을 눌러야 서버에 반영됨.', {
      variant: 'info',
    });
  };

  const saveMonitoringAiSettings = async (monitoringAi?: MonitoringAiSettings) => {
    if (!serverSettings) return;
    const payload: ApiSystemSettings = monitoringAi
      ? { ...serverSettings, monitoring_ai: monitoringAi }
      : serverSettings;
    setMonitoringAiSaving(true);
    try {
      const response = await api.post('/api/settings/system', payload);
      invalidateMonitoringSettingsCache();
      setServerSettings(buildApiSystemSettings(response.data));
      await fetchYoloModelFiles();
      enqueueSnackbar('통합 관제 AI·YOLO 설정이 저장 완료', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || error?.message || '설정 저장에 실패',
        { variant: 'error' }
      );
    } finally {
      setMonitoringAiSaving(false);
    }
  };

  const saveGatewaySettings = async () => {
    if (!serverSettings) return;
    setGatewaySettingsSaving(true);
    try {
      await api.post('/api/settings/system', serverSettings);
      enqueueSnackbar('게이트웨이(실운영 영상) 설정이 저장 완료', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || error?.message || '설정 저장에 실패',
        { variant: 'error' }
      );
    } finally {
      setGatewaySettingsSaving(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSettingChange = (category: string, setting: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [setting]: value
      }
    }));
  };

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/users/');
      const list = extractArrayFromApiPayload(response.data) as Admin[];
      if (!Array.isArray(response.data) && list.length === 0 && response.data != null) {
        console.error('관리자 목록 데이터 형식이 올바르지 않습니다:', response.data);
        enqueueSnackbar('관리자 목록 데이터 형식이 올바르지 않음', { variant: 'error' });
        setAdmins([]);
        return;
      }
      const withoutBrand = list.filter(
        (u) => u?.username !== 'brand-manager' && u?.is_brand_manager !== true
      );
      const adminRows = withoutBrand.filter(
        (u) =>
          u?.role === 'admin' ||
          u?.role === 'manager' ||
          u?.is_superuser === true
      );
      setAdmins(adminRows.length > 0 ? adminRows : withoutBrand);
    } catch (error: any) {
      console.error('관리자 목록 조회 오류:', error);
      enqueueSnackbar(error.response?.data?.detail || '관리자 목록을 불러오기 실패', { variant: 'error' });
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPools = async () => {
    setLoadingPools(true);
    try {
      const response = await api.get('/api/pools/');
      setPools(extractArrayFromApiPayload(response.data) as any[]);
    } catch (error: any) {
      console.error('시설 목록 조회 오류:', error);
      console.error('오류 상세:', error.response?.data);
      enqueueSnackbar(error.response?.data?.message || '시설 목록을 불러오기 실패', { variant: 'error' });
    } finally {
      setLoadingPools(false);
    }
  };

  const fetchBackupSettings = async () => {
    try {
      const { data } = await api.get('/api/backups/settings');
      setSettings((prev) => ({
        ...prev,
        backup: {
          ...prev.backup,
          autoBackup: Boolean(data.auto_backup_enabled),
          backupInterval: data.backup_interval || 'daily',
          backupTime: String(data.backup_time || '02:00').slice(0, 5),
          backupWeekday: data.backup_weekday || 'monday',
          retentionPeriod: Number(data.retention_days ?? prev.backup.retentionPeriod),
          backupLocation: data.backup_location || prev.backup.backupLocation,
        },
      }));
      setNextBackupAt(data.next_backup_at || '');
    } catch (error) {
      console.error('백업 설정 조회 오류:', error);
    }
  };

  const saveBackupSettings = async () => {
    setBackupSettingsSaving(true);
    try {
      const { data } = await api.put('/api/backups/settings', {
        auto_backup_enabled: settings.backup.autoBackup,
        backup_interval: settings.backup.backupInterval,
        backup_time: settings.backup.backupTime,
        backup_weekday: settings.backup.backupWeekday,
        retention_days: settings.backup.retentionPeriod,
        backup_location: settings.backup.backupLocation,
      });
      const saved = data?.settings || data;
      setNextBackupAt(saved?.next_backup_at || '');
      enqueueSnackbar(
        settings.backup.autoBackup ? '자동 백업 설정 저장 완료' : '자동 백업 끔. 설정 저장 완료',
        { variant: 'success' },
      );
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.detail || '자동 백업 설정 저장 실패', { variant: 'error' });
    } finally {
      setBackupSettingsSaving(false);
    }
  };

  const fetchBackupHistory = async (options?: { silent?: boolean }): Promise<any[]> => {
    try {
      const response = await api.get('/api/backups/');
      const list = extractArrayFromApiPayload(response.data) as any[];
      setBackupHistory(list);
      return list;
    } catch (error: any) {
      console.error('백업 기록 조회 오류:', error);
      if (!options?.silent) {
        const detail = error.response?.data?.detail;
        const message =
          (typeof detail === 'string' && detail) ||
          error.response?.data?.message ||
          (error.response?.status === 404
            ? '백업 API를 찾기 불가. 백엔드가 최신 버전인지 확인.'
            : null);
        enqueueSnackbar(message || '백업 기록을 불러오기 실패', { variant: 'error' });
        setBackupHistory([]);
      }
      return [];
    }
  };

  const waitForNewBackupInList = async (knownIds: Set<string>): Promise<boolean> => {
    const pollMs = 3000;
    const maxWaitMs = 30 * 60 * 1000;
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, pollMs));
      const list = await fetchBackupHistory({ silent: true });
      const hasNew = list.some((b) => b?.id != null && !knownIds.has(String(b.id)));
      if (hasNew) return true;
    }
    return false;
  };

  const handleBackup = async () => {
    const knownIds = new Set(backupHistory.map((b) => String(b.id)));
    setIsBackingUp(true);
    try {
      const response = await api.post('/api/backups/');
      if (response.data.status === 'started') {
        enqueueSnackbar('백업이 시작됨. 완료되면 목록에 자동 반영됨', { variant: 'info' });
        const appeared = await waitForNewBackupInList(knownIds);
        if (appeared) {
          enqueueSnackbar('백업이 완료', { variant: 'success' });
        } else {
          enqueueSnackbar(
            '백업이 아직 진행 중이거나 목록 확인에 시간 필요. 잠시 후 새로고침.',
            { variant: 'warning' },
          );
        }
      } else {
        enqueueSnackbar(response.data.message || '백업에 실패', { variant: 'error' });
      }
    } catch (error) {
      console.error('백업 생성 중 오류:', error);
      enqueueSnackbar('백업 생성 중 오류 발생', { variant: 'error' });
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedBackup) return;
    
    setIsRestoring(true);
    try {
      const response = await api.post(`/api/backups/${selectedBackup.id}/restore`);
      if (response.data && response.data.success) {
        enqueueSnackbar('복원이 완료', { variant: 'success' });
        setRestoreDialogOpen(false);
      } else {
        enqueueSnackbar('복원에 실패', { variant: 'error' });
      }
    } catch (error) {
      console.error('복원 오류:', error);
      enqueueSnackbar('복원 중 오류 발생', { variant: 'error' });
    } finally {
      setIsRestoring(false);
    }
  };

  const handleDownloadBackup = async (backupId: string) => {
    try {
      const response = await api.get(`/api/backups/${backupId}/download`, {
        responseType: 'blob'
      });
      
      // 파일 다운로드
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Content-Disposition 헤더에서 파일명 추출
      const contentDisposition = response.headers['content-disposition'];
      let filename = `backup_${backupId}.tar.gz.enc`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('백업 파일 다운로드가 시작됨', { variant: 'success' });
    } catch (error) {
      console.error('백업 다운로드 중 오류:', error);
      enqueueSnackbar('백업 다운로드 중 오류 발생', { variant: 'error' });
    }
  };

  const handleDeleteBackup = async () => {
    if (!backupPendingDelete?.id) return;
    setIsDeletingBackup(true);
    try {
      await api.delete(`/api/backups/${backupPendingDelete.id}`);
      enqueueSnackbar('백업 기록이 삭제 완료', { variant: 'success' });
      setDeleteBackupDialogOpen(false);
      setBackupPendingDelete(null);
      await fetchBackupHistory();
    } catch (error) {
      console.error('백업 삭제 중 오류:', error);
      enqueueSnackbar('백업 삭제 중 오류 발생', { variant: 'error' });
    } finally {
      setIsDeletingBackup(false);
    }
  };

  const handleDeleteAllBackups = async () => {
    setIsDeletingBackup(true);
    try {
      const response = await api.delete('/api/backups/all');
      const count = response.data?.deleted_count ?? 0;
      enqueueSnackbar(`백업 기록 ${count}건이 삭제 완료`, { variant: 'success' });
      setDeleteAllBackupsDialogOpen(false);
      await fetchBackupHistory();
    } catch (error) {
      console.error('백업 전체 삭제 중 오류:', error);
      enqueueSnackbar('백업 전체 삭제 중 오류 발생', { variant: 'error' });
    } finally {
      setIsDeletingBackup(false);
    }
  };

  // 컴포넌트 마운트 시 관리자 목록 조회
  useEffect(() => {
    fetchAdmins();
    fetchServerSettings();
  }, []);

  // 관리자 관리 탭 활성화 시 관리자 목록 조회
  useEffect(() => {
    if (activeTab === 3) { // 관리자 관리 탭이 활성화되었을 때
      fetchAdmins();
    }
  }, [activeTab]);

  // 컴포넌트 마운트 시 시설 목록 조회
  useEffect(() => {
    fetchPools();
  }, []);

  // 시설 비밀번호 관리 탭 활성화 시 시설 목록 조회
  useEffect(() => {
    if (activeTab === 5) { // 시설 비밀번호 관리 탭이 활성화되었을 때
      fetchPools();
    }
  }, [activeTab]);

  // 컴포넌트 마운트 시 백업 기록 조회
  useEffect(() => {
    if (activeTab === 6) { // 데이터 관리 탭이 활성화되었을 때
      fetchBackupHistory();
      void fetchBackupSettings();
    }
  }, [activeTab]);

  const handleAddClick = () => {
    setAddDialogOpen(true);
  };

  const handleEditClick = (admin: Admin) => {
    setSelectedAdmin(admin);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (admin: Admin) => {
    setSelectedAdmin(admin);
    setDeleteDialogOpen(true);
  };

  const handleAddSuccess = () => {
    fetchAdmins();
  };

  const handleEditSuccess = () => {
    fetchAdmins();
  };

  const handleDeleteSuccess = () => {
    fetchAdmins();
  };

  const handlePasswordChange = async () => {
    if (!selectedPool) return;

    if (newPassword !== confirmPassword) {
      setPasswordError('비밀번호가 일치하지 않음');
      return;
    }

    if (newPassword.length < 4) {
      setPasswordError('비밀번호는 최소 4자 이상');
      return;
    }

    try {
      const response = await api.post(`/api/pools/${selectedPool.id}/password`, {
        password: newPassword
      });

      const ok =
        response.status >= 200 &&
        response.status < 300 &&
        (response.data?.success !== false);
      if (ok) {
        enqueueSnackbar('비밀번호가 변경 완료', { variant: 'success' });
        setPasswordDialogOpen(false);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
      } else {
        enqueueSnackbar(
          response.data?.message || '비밀번호 변경에 실패',
          { variant: 'error' }
        );
      }
    } catch (error: any) {
      console.error('비밀번호 변경 오류:', error);
      enqueueSnackbar(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          '비밀번호 변경에 실패',
        { variant: 'error' }
      );
    }
  };

  const handleBulkPasswordChange = async () => {
    if (bulkNewPassword !== bulkConfirmPassword) {
      setBulkPasswordError('비밀번호가 일치하지 않음');
      return;
    }

    if (bulkNewPassword.length < 4) {
      setBulkPasswordError('비밀번호는 최소 4자 이상');
      return;
    }

    setIsUpdating(true);
    let successCount = 0;
    let failCount = 0;

    try {
      // 모든 시설에 대해 비밀번호 변경 요청
      for (const pool of pools) {
        try {
          const response = await api.post(`/api/pools/${pool.id}/password`, {
            password: bulkNewPassword
          });

          const ok =
            response.status >= 200 &&
            response.status < 300 &&
            (response.data?.success !== false);
          if (ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`시설 ${pool.name} 비밀번호 변경 실패:`, error);
          failCount++;
        }
      }

      // 결과 메시지 표시
      if (successCount > 0) {
        enqueueSnackbar(`${successCount}개 시설의 비밀번호가 변경 완료${failCount > 0 ? ` (${failCount}개 실패)` : ''}`, 
          { variant: successCount === pools.length ? 'success' : 'warning' });
      } else {
        enqueueSnackbar('모든 시설의 비밀번호 변경에 실패', { variant: 'error' });
      }

      // 다이얼로그 닫기 및 상태 초기화
      setBulkPasswordDialogOpen(false);
      setBulkNewPassword('');
      setBulkConfirmPassword('');
      setBulkPasswordError('');
    } catch (error) {
      console.error('일괄 비밀번호 변경 오류:', error);
      enqueueSnackbar('일괄 비밀번호 변경 중 오류 발생', { variant: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleControlCenterPasswordChange = async () => {
    if (!controlCenterPassword.current || !controlCenterPassword.new || !controlCenterPassword.confirm) {
      setControlCenterPasswordError('모든 필드를 입력');
      return;
    }

    if (controlCenterPassword.new !== controlCenterPassword.confirm) {
      setControlCenterPasswordError('새 비밀번호가 일치하지 않음');
      return;
    }

    if (controlCenterPassword.new.length < 8) {
      setControlCenterPasswordError('비밀번호는 8자 이상');
      return;
    }

    try {
      const response = await api.put('/api/settings/control-center/password', {
        currentPassword: controlCenterPassword.current,
        newPassword: controlCenterPassword.new
      });

      if (response.data && response.data.success) {
        enqueueSnackbar('관제센터 비밀번호가 변경 완료', { variant: 'success' });
        setIsPasswordDialogOpen(false);
        setControlCenterPassword({ current: '', new: '', confirm: '' });
        setControlCenterPasswordError('');
      } else {
        setControlCenterPasswordError(response.data.message || '비밀번호 변경에 실패');
      }
    } catch (error: any) {
      console.error('비밀번호 변경 오류:', error);
      setControlCenterPasswordError(error.response?.data?.detail || '비밀번호 변경에 실패');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<SettingsIcon />} label="기본 설정" {...hiddenTabProps(systemTabOn(0))} />
          <Tab icon={<SecurityIcon />} label="보안" {...hiddenTabProps(systemTabOn(1))} />
          <Tab icon={<VpnKeyIcon />} label="비밀번호 관리" {...hiddenTabProps(systemTabOn(2))} />
          <Tab icon={<AdminPanelSettingsIcon />} label="관리자 관리" {...hiddenTabProps(systemTabOn(3))} />
          <Tab icon={<WatchIcon />} label="웨어러블 관리" {...hiddenTabProps(systemTabOn(4))} />
          <Tab icon={<PoolIcon />} label="시설 비밀번호" {...hiddenTabProps(systemTabOn(5))} />
          <Tab icon={<StorageIcon />} label="데이터 관리" {...hiddenTabProps(systemTabOn(6))} />
          <Tab icon={<NotificationsIcon />} label="알림" {...hiddenTabProps(systemTabOn(7))} />
          <Tab icon={<AssessmentIcon />} label="모니터링" {...hiddenTabProps(systemTabOn(8))} />
          <Tab icon={<ApiIcon />} label="API 관리" {...hiddenTabProps(systemTabOn(9))} />
          <Tab icon={<BarChartIcon />} label="운영·벤치마크" {...hiddenTabProps(systemTabOn(10))} />
          <Tab icon={<WarningIcon />} label="임계 설정" {...hiddenTabProps(systemTabOn(11))} />
          {isSuperuser && (
            <Tab icon={<ModelTrainingIcon />} label="검출 라벨 · 학습" {...hiddenTabProps(systemTabOn(12))} />
          )}
        </Tabs>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <PrimaryPoolsSettingsCard />
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="시설 게이트웨이 (실운영 영상)"
                subheader="중앙 관제에서 playback_url을 쓸지 여부 (개발/실운영 전환)"
                action={
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={saveGatewaySettings}
                    disabled={gatewaySettingsSaving || !serverSettings}
                  >
                    {gatewaySettingsSaving ? '저장 중…' : '저장'}
                  </Button>
                }
              />
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={serverSettings?.network?.use_remote_playback ?? false}
                      onChange={(e) =>
                        handleGatewayNetworkChange('use_remote_playback', e.target.checked)
                      }
                    />
                  }
                  label="실운영 playback_url 사용 (OFF: 기존 stream_url · 개발)"
                />
                <TextField
                  fullWidth
                  margin="dense"
                  label="공개 API 주소 (playback URL 생성용)"
                  placeholder="https://api.your-domain.com"
                  value={serverSettings?.network?.public_api_base_url ?? ''}
                  onChange={(e) =>
                    handleGatewayNetworkChange('public_api_base_url', e.target.value)
                  }
                  helperText="실운영 도메인(API 주소)을 받으면 여기에 입력. 예: https://api.your-domain.com (끝 슬래시 생략). 비워 두면 서버 환경변수 PUBLIC_API_BASE_URL 사용"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  시설별 게이트웨이 사용·페어링 코드는 왼쪽 메뉴 「시설 관리」→ 해당 시설
                  정보에서 확인·발급
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Divider sx={{ mb: 0 }} />
            <Box
              sx={{
                mt: 3,
                pt: 3,
                px: { xs: 1, sm: 2 },
                pb: 1,
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'action.hover' : 'grey.50',
                borderRadius: 1,
              }}
            >
              <Typography variant="overline" color="text.secondary" sx={{ mb: 2, px: 1, letterSpacing: 1 }}>
                시스템 정보
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardHeader
                      title="시스템 기본 설정"
                      action={
                        <IconButton>
                          <SaveIcon />
                        </IconButton>
                      }
                    />
                    <CardContent>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>언어</InputLabel>
                        <Select
                          value={settings.system.language}
                          label="언어"
                          onChange={(e) =>
                            handleSettingChange('system', 'language', e.target.value)
                          }
                        >
                          <MenuItem value="ko">한국어</MenuItem>
                          <MenuItem value="en">English</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>시간대</InputLabel>
                        <Select
                          value={settings.system.timezone}
                          label="시간대"
                          onChange={(e) =>
                            handleSettingChange('system', 'timezone', e.target.value)
                          }
                        >
                          <MenuItem value="Asia/Seoul">서울 (UTC+9)</MenuItem>
                          <MenuItem value="Asia/Tokyo">도쿄 (UTC+9)</MenuItem>
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardHeader title="시스템 상태" />
                    <CardContent>
                      <List disablePadding>
                        <ListItem disableGutters>
                          <ListItemText primary="시스템 버전" secondary="v1.0.0" />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText
                            primary="마지막 업데이트"
                            secondary={new Date().toLocaleString('ko-KR', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary="시스템 상태" secondary="정상" />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="비밀번호 정책" />
              <CardContent>
                <TextField
                  fullWidth
                  type="number"
                  label="최소 길이"
                  value={settings.security.passwordPolicy.minLength}
                  onChange={(e) => handleSettingChange('security', 'passwordPolicy', {
                    ...settings.security.passwordPolicy,
                    minLength: parseInt(e.target.value)
                  })}
                  sx={{ mb: 2 }}
                />
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Switch
                        checked={settings.security.passwordPolicy.requireSpecialChar}
                        onChange={(e) => handleSettingChange('security', 'passwordPolicy', {
                          ...settings.security.passwordPolicy,
                          requireSpecialChar: e.target.checked
                        })}
                      />
                    </ListItemIcon>
                    <ListItemText primary="특수문자 포함" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Switch
                        checked={settings.security.passwordPolicy.requireNumber}
                        onChange={(e) => handleSettingChange('security', 'passwordPolicy', {
                          ...settings.security.passwordPolicy,
                          requireNumber: e.target.checked
                        })}
                      />
                    </ListItemIcon>
                    <ListItemText primary="숫자 포함" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="세션 관리" />
              <CardContent>
                <TextField
                  fullWidth
                  type="number"
                  label="세션 타임아웃 (분)"
                  value={settings.security.sessionTimeout}
                  onChange={(e) => handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value))}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="최대 로그인 시도 횟수"
                  value={settings.security.maxLoginAttempts}
                  onChange={(e) => handleSettingChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="관제센터 비밀번호" 
                action={
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<VpnKeyIcon />}
                    onClick={() => setIsPasswordDialogOpen(true)}
                  >
                    비밀번호 변경
                  </Button>
                }
              />
              <CardContent>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Switch
                        checked={settings.passwords.controlRoom.enabled}
                        onChange={(e) => handleSettingChange('passwords', 'controlRoom', {
                          ...settings.passwords.controlRoom,
                          enabled: e.target.checked
                        })}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary="비밀번호 보안 활성화" 
                      secondary="관제센터 접근 시 비밀번호 확인"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="마지막 변경일" 
                      secondary={settings.passwords.controlRoom.lastChanged}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="변경 주기" 
                      secondary={`${settings.passwords.controlRoom.changeInterval}일`}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="관리자 비밀번호"
                action={
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<AdminPanelSettingsIcon />}
                  >
                    일괄 변경
                  </Button>
                }
              />
              <CardContent>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Switch
                        checked={settings.passwords.admin.enabled}
                        onChange={(e) => handleSettingChange('passwords', 'admin', {
                          ...settings.passwords.admin,
                          enabled: e.target.checked
                        })}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary="비밀번호 보안 활성화" 
                      secondary="관리자 계정 비밀번호 관리"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="마지막 변경일" 
                      secondary={settings.passwords.admin.lastChanged}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="변경 주기" 
                      secondary={`${settings.passwords.admin.changeInterval}일`}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <AppAccessKeySettingsCard isSuperuser={isSuperuser} />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="관리자 목록"
                action={
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                  >
                    관리자 추가
                  </Button>
                }
              />
              <CardContent>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>이름</TableCell>
                        <TableCell>이메일</TableCell>
                        <TableCell>권한</TableCell>
                        <TableCell>마지막 로그인</TableCell>
                        <TableCell>상태</TableCell>
                        <TableCell>관리</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {admins.map((admin) => (
                        <TableRow key={admin.id}>
                          <TableCell>{admin.full_name || admin.username}</TableCell>
                          <TableCell>{admin.email}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={admin.is_superuser ? '최고관리자' : '일반관리자'}
                                color={admin.is_superuser ? 'error' : 'primary'}
                                size="small"
                              />
                              {!admin.is_superuser && admin.monitor_all_pools ? (
                                <Chip label="시설 전체" color="info" size="small" variant="outlined" />
                              ) : null}
                            </Box>
                          </TableCell>
                          <TableCell>{admin.last_login || '로그인 기록 없음'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={admin.is_active ? '활성' : '비활성'} 
                              color={admin.is_active ? 'success' : 'default'} 
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="수정">
                                <IconButton 
                                  size="small"
                                  onClick={() => handleEditClick(admin)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="삭제">
                                <IconButton 
                                  size="small"
                                  onClick={() => handleDeleteClick(admin)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="웨어러블 모니터링 설정"
                action={
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<WatchIcon />}
                  >
                    새 기기 등록
                  </Button>
                }
              />
              <CardContent>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Switch
                        checked={settings.wearables.monitoring.enabled}
                        onChange={(e) => handleSettingChange('wearables', 'monitoring', {
                          ...settings.wearables.monitoring,
                          enabled: e.target.checked
                        })}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary="모니터링 활성화" 
                      secondary="웨어러블 기기 실시간 모니터링"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="업데이트 주기" 
                      secondary={`${settings.wearables.monitoring.updateInterval}초`}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="알림 임계값 설정" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="심박수 최소"
                      type="number"
                      value={settings.wearables.monitoring.alertThreshold.heartRate.min}
                      onChange={(e) => handleSettingChange('wearables', 'monitoring', {
                        ...settings.wearables.monitoring,
                        alertThreshold: {
                          ...settings.wearables.monitoring.alertThreshold,
                          heartRate: {
                            ...settings.wearables.monitoring.alertThreshold.heartRate,
                            min: parseInt(e.target.value)
                          }
                        }
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="심박수 최대"
                      type="number"
                      value={settings.wearables.monitoring.alertThreshold.heartRate.max}
                      onChange={(e) => handleSettingChange('wearables', 'monitoring', {
                        ...settings.wearables.monitoring,
                        alertThreshold: {
                          ...settings.wearables.monitoring.alertThreshold,
                          heartRate: {
                            ...settings.wearables.monitoring.alertThreshold.heartRate,
                            max: parseInt(e.target.value)
                          }
                        }
                      })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="산소포화도 최소"
                      type="number"
                      value={settings.wearables.monitoring.alertThreshold.spo2.min}
                      onChange={(e) => handleSettingChange('wearables', 'monitoring', {
                        ...settings.wearables.monitoring,
                        alertThreshold: {
                          ...settings.wearables.monitoring.alertThreshold,
                          spo2: {
                            ...settings.wearables.monitoring.alertThreshold.spo2,
                            min: parseInt(e.target.value)
                          }
                        }
                      })}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={5}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="시설 비밀번호 관리"
                action={
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<PoolIcon />}
                    onClick={() => setBulkPasswordDialogOpen(true)}
                  >
                    일괄 변경
                  </Button>
                }
              />
              <CardContent>
                {loadingPools ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>시설 코드</TableCell>
                          <TableCell>시설명</TableCell>
                          <TableCell>마지막 변경일</TableCell>
                          <TableCell>상태</TableCell>
                          <TableCell>관리</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pools.map((pool) => (
                          <TableRow key={pool.id}>
                            <TableCell>{pool.code}</TableCell>
                            <TableCell>{pool.name}</TableCell>
                            <TableCell>
                              {pool.updated_at ? new Date(pool.updated_at).toLocaleDateString('ko-KR') : '변경 기록 없음'}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={pool.is_active ? '활성' : '비활성'} 
                                color={pool.is_active ? 'success' : 'default'} 
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="비밀번호 변경">
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      setSelectedPool(pool);
                                      setPasswordDialogOpen(true);
                                    }}
                                  >
                                    <VpnKeyIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="상세 정보">
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      setSelectedPool(pool);
                                      setDetailDialogOpen(true);
                                    }}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={6}>
        <Grid container spacing={3}>
          {allowNav('system.data.auto-backup') && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="자동 백업 설정"
                action={
                  <Button 
                    startIcon={<BackupIcon />} 
                    variant="contained" 
                    color="primary"
                    onClick={handleBackup}
                    disabled={isBackingUp}
                  >
                    {isBackingUp ? '백업 중...' : '지금 백업'}
                  </Button>
                }
              />
              <CardContent>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Switch
                        checked={settings.backup.autoBackup}
                        onChange={(e) => handleSettingChange('backup', 'autoBackup', e.target.checked)}
                      />
                    </ListItemIcon>
                    <ListItemText primary="자동 백업 활성화" />
                  </ListItem>
                </List>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>백업 주기</InputLabel>
                  <Select
                    value={settings.backup.backupInterval}
                    label="백업 주기"
                    onChange={(e) => handleSettingChange('backup', 'backupInterval', e.target.value)}
                  >
                    <MenuItem value="hourly">매시간</MenuItem>
                    <MenuItem value="daily">매일</MenuItem>
                    <MenuItem value="weekly">매주</MenuItem>
                  </Select>
                </FormControl>
                {settings.backup.backupInterval === 'weekly' && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>요일</InputLabel>
                    <Select
                      value={settings.backup.backupWeekday}
                      label="요일"
                      onChange={(e) => handleSettingChange('backup', 'backupWeekday', e.target.value)}
                    >
                      <MenuItem value="monday">월요일</MenuItem>
                      <MenuItem value="tuesday">화요일</MenuItem>
                      <MenuItem value="wednesday">수요일</MenuItem>
                      <MenuItem value="thursday">목요일</MenuItem>
                      <MenuItem value="friday">금요일</MenuItem>
                      <MenuItem value="saturday">토요일</MenuItem>
                      <MenuItem value="sunday">일요일</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <TextField
                  fullWidth
                  type="time"
                  label={settings.backup.backupInterval === 'hourly' ? '실행 분 (시 무시)' : '실행 시각'}
                  value={settings.backup.backupTime}
                  onChange={(e) => handleSettingChange('backup', 'backupTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 60 }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="보관 기간 (일)"
                  value={settings.backup.retentionPeriod}
                  onChange={(e) => handleSettingChange('backup', 'retentionPeriod', parseInt(e.target.value))}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => void saveBackupSettings()}
                  disabled={backupSettingsSaving}
                >
                  {backupSettingsSaving ? '저장 중' : '자동 백업 설정 저장'}
                </Button>
                <Alert severity="info" sx={{ mt: 2 }}>
                  {settings.backup.backupInterval === 'hourly'
                    ? `매시 ${String(settings.backup.backupTime || '02:00').slice(3, 5)}분 실행.`
                    : settings.backup.backupInterval === 'weekly'
                      ? '매주 지정 요일·시각 실행.'
                      : '매일 지정 시각 실행.'}
                  {' '}결과는 백업 기록에 표시. 백엔드 실행 중에만 동작.
                  {nextBackupAt ? ` 다음 실행 ${nextBackupAt}.` : ''}
                </Alert>
              </CardContent>
            </Card>
          </Grid>
          )}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="백업 위치 설정" />
              <CardContent>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>백업 저장소</InputLabel>
                  <Select
                    value={settings.backup.backupLocation}
                    label="백업 저장소"
                    onChange={(e) => handleSettingChange('backup', 'backupLocation', e.target.value)}
                  >
                    <MenuItem value="local">로컬 저장소</MenuItem>
                    <MenuItem value="cloud">클라우드 저장소</MenuItem>
                    <MenuItem value="both">로컬 + 클라우드</MenuItem>
                  </Select>
                </FormControl>
                <Alert severity="info" sx={{ mt: 2 }}>
                  클라우드 저장소를 선택하면 자동으로 암호화되어 저장됨.
                </Alert>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="백업 기록"
                action={
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="목록 새로고침">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => void fetchBackupHistory()}
                          disabled={isBackingUp}
                          aria-label="백업 기록 새로고침"
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      disabled={backupHistory.length === 0 || isDeletingBackup || isBackingUp}
                      onClick={() => setDeleteAllBackupsDialogOpen(true)}
                    >
                      전체 삭제
                    </Button>
                  </Box>
                }
              />
              <CardContent>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>백업 일시</TableCell>
                        <TableCell>구분</TableCell>
                        <TableCell>크기</TableCell>
                        <TableCell>저장 위치</TableCell>
                        <TableCell>상태</TableCell>
                        <TableCell>관리</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {backupHistory.map((backup) => (
                        <TableRow key={backup.id}>
                          <TableCell>
                            {new Date(backup.created_at).toLocaleString('ko-KR')}
                          </TableCell>
                          <TableCell>{backup.description || '이전 백업'}</TableCell>
                          <TableCell>{backup.size}</TableCell>
                          <TableCell>{backup.location}</TableCell>
                          <TableCell>
                            <Chip 
                              label={backup.status === 'success' || backup.status === 'completed' || backup.status === 'started' ? '성공' : '실패'} 
                              color={backup.status === 'success' || backup.status === 'completed' || backup.status === 'started' ? 'success' : 'error'} 
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="다운로드">
                                <IconButton 
                                  size="small"
                                  onClick={() => handleDownloadBackup(backup.id)}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="복원">
                                <IconButton 
                                  size="small"
                                  onClick={() => {
                                    setSelectedBackup(backup);
                                    setRestoreDialogOpen(true);
                                  }}
                                >
                                  <RestoreIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="삭제">
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={isDeletingBackup}
                                  onClick={() => {
                                    setBackupPendingDelete(backup);
                                    setDeleteBackupDialogOpen(true);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={7}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title="알림 채널 설정"
                subheader="통합 관제 현장 안전 알림 — 사용할 채널을 선택한 뒤 저장."
                action={
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => void saveNotificationChannelSettings()}
                    disabled={notificationChannelsSaving || !serverSettings}
                  >
                    {notificationChannelsSaving ? '저장 중…' : '저장'}
                  </Button>
                }
              />
              <CardContent>
                {serverSettings ? (
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Switch
                          checked={serverSettings.notifications.safety_channel_sms}
                          onChange={(e) =>
                            handleNotificationChannelChange(
                              'safety_channel_sms',
                              e.target.checked
                            )
                          }
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="현장 안전 SMS (알리고)"
                        secondary="시설 safety_phone 수신 · 아래 블록에서 알리고/웹훅 설정"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Switch
                          checked={serverSettings.notifications.safety_channel_kakao}
                          onChange={(e) =>
                            handleNotificationChannelChange(
                              'safety_channel_kakao',
                              e.target.checked
                            )
                          }
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="카카오 알림톡"
                        secondary="아래 「카카오 알림톡」 블록에서 연동 설정"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Switch
                          checked={serverSettings.notifications.safety_channel_wearable}
                          onChange={(e) =>
                            handleNotificationChannelChange(
                              'safety_channel_wearable',
                              e.target.checked
                            )
                          }
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="웨어러블"
                        secondary="시설별 알림용 웨어러블 등록 · WebSocket"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Switch
                          checked={
                            serverSettings.notifications.event_share_image_enabled
                          }
                          onChange={(e) =>
                            handleNotificationChannelChange(
                              'event_share_image_enabled',
                              e.target.checked
                            )
                          }
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="합성 이미지 서비스"
                        secondary="이벤트 전송 시 캡처+메시지 JPEG 생성 · 폴더 열기 (카카오 수동 첨부용)"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Switch
                          checked={serverSettings.notifications.kakao_bot_send_enabled}
                          onChange={(e) =>
                            handleNotificationChannelChange(
                              'kakao_bot_send_enabled',
                              e.target.checked
                            )
                          }
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="카카오 봇 보내기"
                        secondary="이벤트 전송 시 카카오 실행→연락처 `이름:번호`로 친구 검색·채팅·이미지 순차 전송 (PC는 전화번호 검색이 잘 안 됨)"
                      />
                    </ListItem>
                  </List>
                ) : (
                  <CircularProgress size={24} />
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="알림 수준 설정" />
              <CardContent>
                <FormControl fullWidth>
                  <InputLabel>알림 수준</InputLabel>
                  <Select
                    value={settings.notifications.alertLevel}
                    label="알림 수준"
                    onChange={(e) => handleSettingChange('notifications', 'alertLevel', e.target.value)}
                  >
                    <MenuItem value="low">낮음 (중요 알림만)</MenuItem>
                    <MenuItem value="medium">중간 (일반 알림 포함)</MenuItem>
                    <MenuItem value="high">높음 (모든 알림)</MenuItem>
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1, mb: 0 }}>
              현장 안전 멀티 채널 알림
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              SMS · 카카오 알림톡 · 웨어러블 — 채널별 블록에서 설정 후 각 카드의 저장을 누름.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {serverSettings && (
              <SafetySmsSettingsCard
                cardSx={safetyAlertSettingsCardSx('sms')}
                value={serverSettings.safety_sms ?? DEFAULT_SAFETY_SMS_SETTINGS}
                onChange={handleSafetySmsChange}
                onSave={saveSafetySmsSettings}
                onResetDefaults={resetSafetySmsToDefaults}
                saving={safetySmsSaving}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {serverSettings && (
              <SafetyKakaoSettingsCard
                cardSx={safetyAlertSettingsCardSx('kakao')}
                value={serverSettings.safety_kakao ?? DEFAULT_SAFETY_KAKAO_SETTINGS}
                onChange={handleSafetyKakaoChange}
                onSave={saveSafetyKakaoSettings}
                onResetDefaults={resetSafetyKakaoToDefaults}
                saving={safetyKakaoSaving}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {serverSettings && (
              <WearableAlertSettingsCard
                cardSx={safetyAlertSettingsCardSx('wearable')}
                value={serverSettings.wearable_alert ?? DEFAULT_WEARABLE_ALERT_SETTINGS}
                onChange={handleWearableAlertChange}
                onSave={saveWearableAlertSettings}
                onResetDefaults={resetWearableAlertToDefaults}
                saving={wearableAlertSaving}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {serverSettings && (
              <ControlRoomSirenSettingsCard
                value={
                  serverSettings.control_room_siren ?? DEFAULT_CONTROL_ROOM_SIREN_SETTINGS
                }
                onChange={handleControlRoomSirenChange}
                onSave={() => void saveControlRoomSirenSettings()}
                onResetDefaults={resetControlRoomSirenToDefaults}
                saving={controlRoomSirenSaving}
                showSnackbar={(message, severity) =>
                  enqueueSnackbar(message, { variant: severity })
                }
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <SafetyAlertDispatchHistoryCard
              showPoolColumn
              pageSize={5}
              title="현장 안전 알림 발송 이력"
              subheader="SMS · 카카오 알림톡 · 웨어러블 — 전체 시설 발송 결과 (최근순)"
            />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={8}>
        <AiServerResourceMonitor />
      </TabPanel>

      <TabPanel value={activeTab} index={9}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="API 키 관리" />
              <CardContent>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<ApiIcon />}
                  sx={{ mb: 2 }}
                >
                  새 API 키 생성
                </Button>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="프로덕션 API 키" 
                      secondary="****-****-****-1234" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="개발용 API 키" 
                      secondary="****-****-****-5678" 
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="API 사용량" />
              <CardContent>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="일일 요청 수" 
                      secondary="1,234 / 10,000" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="월간 요청 수" 
                      secondary="25,678 / 100,000" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="데이터 전송량" 
                      secondary="2.5GB / 10GB" 
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={10}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          파이프라인 지연·시스템 준비 상태·익명 벤치마크 등 운영 참고 지표. 일상 관제 메뉴와
          분리해 둠
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SystemReadinessWidget />
          </Grid>
          <Grid item xs={12} md={6}>
            <EventPipelineLatencyWidget />
          </Grid>
          <Grid item xs={12}>
            <AnonymousBenchmarkWidget />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={11}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          임계 설정
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          AI 검출·위험 판정·증거 보관 등 운영 임계를 한곳에서 관리. 시설·CCTV에서 비운
          항목은 여기 기본값을 승계. (익사 motion 민감도/정지는 시설·CCTV 화면에서 별도)
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          CCTV별 「검출 신뢰도 하한/연속 프레임」은 CCTV 관리 → 카메라 편집 → 사람 검출(YOLO · 이 카메라만)
          에서 설정. 우선순위는 CCTV → 시설 → 시스템.
        </Alert>

        {serverSettings ? (
          <ThresholdUnifyCard
            riskThresholds={mergeRiskLandThresholds(serverSettings.risk_thresholds)}
            monitoringAi={serverSettings.monitoring_ai}
            poolCount={pools.length}
            onDone={(stats) => {
              if (stats.pools_updated > 0 || stats.cctvs_updated > 0) {
                enqueueSnackbar(
                  `시설·CCTV 임계를 시스템 값으로 통일함. (시설 ${stats.pools_updated} · CCTV ${stats.cctvs_updated})`,
                  { variant: 'success' }
                );
              } else {
                enqueueSnackbar('변경할 시설·CCTV 임계 오버라이드 없음', {
                  variant: 'info',
                });
              }
            }}
          />
        ) : null}

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="overline"
            color="primary"
            sx={{ letterSpacing: 1, fontWeight: 700, display: 'block', mb: 1 }}
          >
            1. 위험 판정 임계
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            배회·특별관리구역 체류·밀집·육상 알림 쿨다운 등
          </Typography>
          {serverSettings && (
            <RiskLandThresholdsCard
              value={mergeRiskLandThresholds(serverSettings.risk_thresholds)}
              onChange={handleRiskThresholdsChange}
              onSave={saveRiskThresholdsSettings}
              onResetDefaults={resetRiskThresholdsToDefaults}
              saving={riskThresholdsSaving}
            />
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="overline"
            color="primary"
            sx={{ letterSpacing: 1, fontWeight: 700, display: 'block', mb: 1 }}
          >
            2. AI 검출 · 관제 루프
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            YOLO 가중치·파이프라인과 중앙 관제 AI 루프(간격·신뢰도·트랙 등)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {serverSettings && (
                <YoloDetectorSettingsCard
                  value={serverSettings.monitoring_ai}
                  onChange={handleMonitoringAiChange}
                  onSave={() => saveMonitoringAiSettings(serverSettings.monitoring_ai)}
                  saving={monitoringAiSaving}
                  modelFiles={yoloModelFiles}
                  yoloStatus={yoloRuntimeStatus as React.ComponentProps<
                    typeof YoloDetectorSettingsCard
                  >['yoloStatus']}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              {serverSettings && (
                <MonitoringAiSettingsCard
                  value={serverSettings.monitoring_ai}
                  onChange={handleMonitoringAiChange}
                  onSave={saveMonitoringAiSettings}
                  onResetDefaults={resetMonitoringAiToDefaults}
                  saving={monitoringAiSaving}
                />
              )}
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="overline"
            color="primary"
            sx={{ letterSpacing: 1, fontWeight: 700, display: 'block', mb: 1 }}
          >
            3. 증거 영상 보관
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            위험 감지 시점 전·후 HLS/증거 클립 길이
          </Typography>
          {serverSettings && (
            <RiskRecordingSettingsCard
              value={serverSettings.risk_recording}
              onChange={handleRiskRecordingChange}
              onSave={saveRiskRecordingSettings}
              onResetDefaults={resetRiskRecordingToDefaults}
              saving={riskRecordingSaving}
            />
          )}
        </Box>
      </TabPanel>

      {isSuperuser ? (
        <TabPanel value={activeTab} index={12}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            검출 라벨 · 학습
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            관제 오검출/미검출 라벨로 pool_swimmer 학습. 최고관리자 전용. 일상 관제에는
            필수 아님.
          </Typography>
          <DetectionLabelTrainingCard
            pools={pools}
            defaultYoloModel={serverSettings?.monitoring_ai?.default_yolo_model}
          />
        </TabPanel>
      ) : null}

      <ChangeControlCenterPasswordDialog
        open={isPasswordDialogOpen}
        onClose={() => {
          setIsPasswordDialogOpen(false);
          setControlCenterPassword({ current: '', new: '', confirm: '' });
          setControlCenterPasswordError('');
        }}
      >
        <DialogTitle>관제센터 비밀번호 변경</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            관제센터 접근 비밀번호를 변경
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="현재 비밀번호"
            type="password"
            fullWidth
            value={controlCenterPassword.current}
            onChange={(e) => setControlCenterPassword({ ...controlCenterPassword, current: e.target.value })}
            error={!!controlCenterPasswordError}
          />
          <TextField
            margin="dense"
            label="새 비밀번호"
            type="password"
            fullWidth
            value={controlCenterPassword.new}
            onChange={(e) => setControlCenterPassword({ ...controlCenterPassword, new: e.target.value })}
            error={!!controlCenterPasswordError}
          />
          <TextField
            margin="dense"
            label="새 비밀번호 확인"
            type="password"
            fullWidth
            value={controlCenterPassword.confirm}
            onChange={(e) => setControlCenterPassword({ ...controlCenterPassword, confirm: e.target.value })}
            error={!!controlCenterPasswordError}
            helperText={controlCenterPasswordError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsPasswordDialogOpen(false);
            setControlCenterPassword({ current: '', new: '', confirm: '' });
            setControlCenterPasswordError('');
          }}>
            취소
          </Button>
          <Button onClick={handleControlCenterPasswordChange} variant="contained" color="primary">
            변경
          </Button>
        </DialogActions>
      </ChangeControlCenterPasswordDialog>

      <AddAdminDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={handleAddSuccess}
      />

      <EditAdminDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleEditSuccess}
        admin={selectedAdmin ? {
          id: selectedAdmin.id,
          username: selectedAdmin.username,
          email: selectedAdmin.email,
          name: selectedAdmin.full_name,
          role: selectedAdmin.role,
          is_active: selectedAdmin.is_active,
          is_superuser: selectedAdmin.is_superuser,
          is_main_admin: !!selectedAdmin.is_main_admin,
          last_login: selectedAdmin.last_login,
          monitoring_pool_ids: selectedAdmin.monitoring_pool_ids,
          monitor_all_pools: selectedAdmin.monitor_all_pools,
        } : null}
      />

      <DeleteAdminDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onSuccess={handleDeleteSuccess}
        adminId={selectedAdmin?.id || null}
        adminName={selectedAdmin?.full_name || selectedAdmin?.username || ''}
      />

      {/* 비밀번호 변경 다이얼로그 */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
        <DialogTitle>시설 비밀번호 변경</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {selectedPool?.name} 시설 비밀번호 변경.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="새 비밀번호"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError}
          />
          <TextField
            margin="dense"
            label="비밀번호 확인"
            type="password"
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={!!passwordError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPasswordDialogOpen(false);
            setNewPassword('');
            setConfirmPassword('');
            setPasswordError('');
          }}>
            취소
          </Button>
          <Button onClick={handlePasswordChange} variant="contained" color="primary">
            변경
          </Button>
        </DialogActions>
      </Dialog>

      {/* 상세 정보 다이얼로그 */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>시설 상세 정보</DialogTitle>
        <DialogContent>
          {selectedPool && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>기본 정보</Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="시설 코드" 
                      secondary={selectedPool.code} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="시설명" 
                      secondary={selectedPool.name} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="위치" 
                      secondary={selectedPool.location} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="주소" 
                      secondary={selectedPool.address} 
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>관리 정보</Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="관리자" 
                      secondary={selectedPool.manager_name} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="연락처" 
                      secondary={selectedPool.manager_phone} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="안전연락처" 
                      secondary={selectedPool.safety_phone} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="이메일" 
                      secondary={selectedPool.email} 
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>운영 정보</Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="수용 인원" 
                      secondary={`${selectedPool.current_occupancy || 0} / ${selectedPool.capacity}`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="시설 유형" 
                      secondary={selectedPool.type === 'INDOOR' ? '실내' : '실외'} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="조도 센서" 
                      secondary={selectedPool.illuminance_sensor ? '설치됨' : '미설치'} 
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 일괄 비밀번호 변경 다이얼로그 */}
      <Dialog open={bulkPasswordDialogOpen} onClose={() => setBulkPasswordDialogOpen(false)}>
        <DialogTitle>시설 비밀번호 일괄 변경</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            모든 시설의 비밀번호를 동일하게 변경. ({pools.length}개 시설)
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="새 비밀번호"
            type="password"
            fullWidth
            value={bulkNewPassword}
            onChange={(e) => setBulkNewPassword(e.target.value)}
            error={!!bulkPasswordError}
            helperText={bulkPasswordError}
            disabled={isUpdating}
          />
          <TextField
            margin="dense"
            label="비밀번호 확인"
            type="password"
            fullWidth
            value={bulkConfirmPassword}
            onChange={(e) => setBulkConfirmPassword(e.target.value)}
            error={!!bulkPasswordError}
            disabled={isUpdating}
          />
          {isUpdating && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setBulkPasswordDialogOpen(false);
              setBulkNewPassword('');
              setBulkConfirmPassword('');
              setBulkPasswordError('');
            }}
            disabled={isUpdating}
          >
            취소
          </Button>
          <Button 
            onClick={handleBulkPasswordChange} 
            variant="contained" 
            color="primary"
            disabled={isUpdating}
          >
            일괄 변경
          </Button>
        </DialogActions>
      </Dialog>

      {/* 백업 단건 삭제 확인 */}
      <Dialog
        open={deleteBackupDialogOpen}
        onClose={() => !isDeletingBackup && setDeleteBackupDialogOpen(false)}
      >
        <DialogTitle>백업 기록 삭제</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {backupPendingDelete && (
              <>
                다음 백업 기록을 삭제 확인
                <br />
                백업 일시:{' '}
                {new Date(backupPendingDelete.created_at).toLocaleString('ko-KR')}
                <br />
                삭제 후에는 복원·다운로드 불가
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteBackupDialogOpen(false);
              setBackupPendingDelete(null);
            }}
            disabled={isDeletingBackup}
          >
            취소
          </Button>
          <Button
            onClick={handleDeleteBackup}
            variant="contained"
            color="error"
            disabled={isDeletingBackup}
          >
            {isDeletingBackup ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 백업 전체 삭제 확인 */}
      <Dialog
        open={deleteAllBackupsDialogOpen}
        onClose={() => !isDeletingBackup && setDeleteAllBackupsDialogOpen(false)}
      >
        <DialogTitle>백업 기록 전체 삭제</DialogTitle>
        <DialogContent>
          <DialogContentText>
            저장된 백업 기록 {backupHistory.length}건 전체 삭제 확인
            <br />
            이 작업은 되돌리기 불가
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteAllBackupsDialogOpen(false)}
            disabled={isDeletingBackup}
          >
            취소
          </Button>
          <Button
            onClick={handleDeleteAllBackups}
            variant="contained"
            color="error"
            disabled={isDeletingBackup}
          >
            {isDeletingBackup ? '삭제 중...' : '전체 삭제'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 복원 확인 다이얼로그 */}
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
        <DialogTitle>백업 복원</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedBackup && (
              <>
                다음 백업을 복원 확인<br />
                백업 일시: {new Date(selectedBackup.created_at).toLocaleString('ko-KR')}<br />
                현재 데이터는 모두 삭제되고 백업 시점의 데이터로 복원됨.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setRestoreDialogOpen(false)}
            disabled={isRestoring}
          >
            취소
          </Button>
          <Button 
            onClick={handleRestore}
            variant="contained" 
            color="primary"
            disabled={isRestoring}
          >
            {isRestoring ? '복원 중...' : '복원'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemManagement; 